import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Target, Loader2, Sparkles, RefreshCw, Heart, MessageCircle, Repeat2, Check,
} from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GrowInput } from "@/components/ui/grow-input";
import { Label } from "@/components/ui/label";
import NoProject from "@/components/NoProject";
import JourneyBar from "@/components/JourneyBar";
import { GoalsMethodCard } from "@/components/goals/GoalsMethodCard";
import { KeyResultsCard } from "@/components/goals/KeyResultsCard";
import { ActivityKpisCard, ApproveGoalsButton } from "@/components/goals/ActivityKpisCard";
import { StageNav } from "@/components/StageNav";
import { DesiredOutcomeCard, CampaignSuggestionsCard } from "@/components/goals/CampaignSuggestionsCard";
import { toast } from "sonner";

const METRIC_META = {
  likes: { icon: Heart, color: "text-rose-400", bg: "bg-rose-500/10 border-rose-500/20" },
  comments: { icon: MessageCircle, color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/20" },
  reposts: { icon: Repeat2, color: "text-accent", bg: "bg-accent/10 border-accent/20" },
};

export default function Goals() {
  const { activeProject } = useProjects();
  const navigate = useNavigate();
  const location = useLocation();
  const [objective, setObjective] = useState("");
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [nextCycle, setNextCycle] = useState(false);
  const [savedProposed, setSavedProposed] = useState(null);
  const [channels, setChannels] = useState([]);
  const [keyResults, setKeyResults] = useState([]);
  const [activityKpis, setActivityKpis] = useState([]);
  const [approved, setApproved] = useState(false);
  const [approvedAt, setApprovedAt] = useState(null);
  const [desired, setDesired] = useState("");
  const [campaignIdeas, setCampaignIdeas] = useState([]);

  const CHANNELS = ["Instagram", "TikTok", "Snapchat", "X", "YouTube", "LinkedIn"];
  const toggleChannel = (c) => {
    setChannels((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
    setSaved(false);
  };

  useEffect(() => {
    if (!activeProject) { setFetching(false); return; }
    if (location.state?.proposed) {
      const p = location.state.proposed;
      setObjective(p.objective || "");
      setKpis(p.kpis || []);
      setKeyResults(p.key_results || []);
      setActivityKpis(p.activity_kpis || []);
      setCampaignIdeas(p.campaigns || []);
      setApproved(false);
      setSaved(false);
      setNextCycle(true);
      setFetching(false);
      window.history.replaceState({}, "");
      return;
    }
    setFetching(true); setKpis([]); setObjective(""); setSaved(false); setNextCycle(false); setSavedProposed(null);
    setKeyResults([]); setActivityKpis([]); setApproved(false); setApprovedAt(null); setDesired(""); setCampaignIdeas([]);
    api.get(`/projects/${activeProject.id}/goals`)
      .then((r) => {
        setObjective(r.data.objective || ""); setKpis(r.data.kpis || []); setChannels(r.data.channels || []);
        setKeyResults(r.data.key_results || []); setActivityKpis(r.data.activity_kpis || []);
        setDesired(r.data.desired_outcome || ""); setCampaignIdeas(r.data.campaigns || []);
        setApproved(r.data.approved !== false); setApprovedAt(r.data.approved_at || r.data.updated_at || null);
        setSaved(true); setSavedProposed(r.data.proposed_next || null);
      })
      .catch(() => {})
      .finally(() => setFetching(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject]);

  const suggest = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/projects/${activeProject.id}/goals/suggest`, { desired_outcome: desired });
      setObjective(data.objective || "");
      setKpis(data.kpis || []);
      setKeyResults(data.key_results || []);
      setActivityKpis(data.activity_kpis || []);
      setCampaignIdeas(data.campaigns || []);
      setApproved(false);
      setSaved(false);
      toast.success("تم اقتراح أهداف ونتائج ومؤشرات (OKR/KPI) ✨ — راجعها ثم اعتمدها");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "فشل اقتراح الأهداف");
    } finally { setLoading(false); }
  };

  const updateKpi = (i, field, val) => {
    const next = [...kpis];
    next[i] = { ...next[i], [field]: val };
    setKpis(next);
    setSaved(false);
  };

  const useProposed = () => {
    if (!savedProposed) return;
    setObjective(savedProposed.objective || "");
    setKpis(savedProposed.kpis || []);
    setKeyResults(savedProposed.key_results || []);
    setActivityKpis(savedProposed.activity_kpis || []);
    setApproved(false);
    setSaved(false);
    setNextCycle(true);
    setSavedProposed(null);
    toast.success("تم تحميل أهداف الدورة القادمة — راجعها واعتمدها");
  };

  const save = async (approve) => {
    if (!kpis.length) return toast.error("لا توجد مؤشرات لاعتمادها");
    if (approve && !objective.trim()) return toast.error("اكتب الهدف (Objective) أولاً");
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${activeProject.id}/goals`, {
        objective,
        kpis: kpis.map((k) => ({ ...k, target: Number(k.target) || 0 })),
        key_results: keyResults.filter((k) => k.label?.trim()).map((k) => ({ ...k, target: Number(k.target) || 0 })),
        activity_kpis: activityKpis.filter((k) => k.label?.trim()).map((k) => ({ ...k, target: Number(k.target) || 0 })),
        campaigns: campaignIdeas.filter((c) => c.name?.trim()),
        desired_outcome: desired,
        channels,
        approved: !!approve,
      });
      setSaved(true);
      setApproved(!!approve);
      setApprovedAt(data.approved_at || data.updated_at || null);
      toast.success(approve ? "تم اعتماد الأهداف ✅ — ستُراعى في تجهيز الحملات والمحتوى" : "تم حفظ الأهداف كمسودة");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };
  const mark = (setter) => (v) => { setter(v); setSaved(false); };

  if (!activeProject) return <NoProject message="اختر مشروعاً لتحديد أهدافه ومؤشراته." />;

  if (activeProject.standalone) {
    return (
      <div className="glass rounded-2xl p-12 text-center border border-border max-w-xl mx-auto" data-testid="strategy-only-guard">
        <Target className="w-10 h-10 text-primary mx-auto mb-4" />
        <h3 className="text-xl font-bold mb-2">هذا القسم لمشاريع الاستراتيجية</h3>
        <p className="text-muted-foreground mb-6">«{activeProject.name}» مشروع محتوى. استخدم استوديو المحتوى للتوليد، أو أنشئ مشروع استراتيجية للوصول لهذا القسم.</p>
        <Button onClick={() => navigate("/app/studio")} className="bg-primary hover:bg-primary/90 text-white gap-2" data-testid="guard-go-studio"><Sparkles className="w-4 h-4" /> الانتقال لاستوديو المحتوى</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="goals-page">
      <JourneyBar current="goals" />

      {nextCycle && (
        <div className="glass rounded-2xl p-4 border border-accent/30 bg-accent/5 flex items-center gap-3" data-testid="next-cycle-banner">
          <RefreshCw className="w-5 h-5 text-accent shrink-0" />
          <p className="text-sm">هذه أهداف مُقترحة <b>للدورة القادمة</b> مبنية على نتائج قياسك السابق — راجع الأرقام ثم اعتمدها لبدء الدورة الجديدة.</p>
        </div>
      )}

      {savedProposed && !nextCycle && (
        <div className="glass rounded-2xl p-4 border border-primary/30 bg-primary/5 flex items-center justify-between gap-4 flex-wrap" data-testid="saved-proposed-banner">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-5 h-5 text-primary shrink-0" />
            <p className="text-sm">لديك <b>أهداف مقترحة للدورة القادمة</b> جاهزة بناءً على نتائج دورتك السابقة.</p>
          </div>
          <Button size="sm" onClick={useProposed} className="bg-primary hover:bg-primary/90 text-white gap-1" data-testid="use-proposed-btn">
            <RefreshCw className="w-3.5 h-3.5" /> استخدم أهداف الدورة القادمة
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold font-heading">الأهداف والمؤشرات (OKR + KPI)</h1>
          <p className="text-muted-foreground">هدف طموح، نتائج قابلة للتحقق، ومؤشرات أداء شهرية لـ «{activeProject.name}»</p>
        </div>
        <Button onClick={suggest} disabled={loading} className="bg-primary hover:bg-primary/90 text-white gap-2" data-testid="suggest-goals-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : kpis.length ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
          {kpis.length ? "إعادة الاقتراح" : "اقترح أهدافاً بالذكاء الاصطناعي"}
        </Button>
      </div>

      {!fetching && <DesiredOutcomeCard value={desired} onChange={(v) => { setDesired(v); setSaved(false); }} />}

      {fetching ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : loading && !kpis.length ? (
        <div className="glass rounded-2xl p-16 text-center border border-border">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">يحلّل الذكاء الاصطناعي استراتيجيتك ويقترح أهدافاً واقعية…</p>
        </div>
      ) : !kpis.length ? (
        <div className="glass rounded-2xl p-16 text-center border border-border" data-testid="goals-empty">
          <Target className="w-12 h-12 text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">اكتب ما تريد الوصول إليه أعلاه ثم اضغط «اقترح أهدافاً» — يحلّل الذكاء الاصطناعي استراتيجيتك ويقترح هدفاً ونتائج ومؤشرات وحملات مناسبة.</p>
        </div>
      ) : (
        <div className="space-y-5">
          <GoalsMethodCard />
          {approved && saved && (
            <div className="glass rounded-2xl p-4 border border-emerald-500/30 bg-emerald-500/5 flex items-start gap-3" data-testid="goals-approved-banner">
              <Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed">
                <b>أهدافك معتمدة</b>{approvedAt ? ` منذ ${new Date(approvedAt).toLocaleDateString("ar-SA")}` : ""} — نراعي استراتيجيتك وهذه الأهداف في تجهيز الحملات وصناعة كل منشور لتحقق نتائجك وفق أفضل الممارسات.
              </p>
            </div>
          )}
          <div className="glass rounded-2xl p-6 border border-border" data-testid="goals-objective">
            <Label className="mb-2 block font-bold">الهدف (Objective) للدورة</Label>
            <GrowInput
              value={objective}
              onChange={(e) => { setObjective(e.target.value); setSaved(false); }}
              className="bg-secondary border-border font-semibold"
              data-testid="goals-objective-input"
              placeholder="مثال: ترسيخ العلامة وزيادة المبيعات عبر المحتوى القصير"
            />
          </div>

          <KeyResultsCard items={keyResults} onChange={mark(setKeyResults)} />

          <div className="glass rounded-2xl p-6 border border-border" data-testid="goals-channels">
            <Label className="mb-3 block font-bold">القنوات المستهدفة (اختر واحدة أو أكثر)</Label>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map((c) => {
                const on = channels.includes(c);
                return (
                  <button key={c} type="button" onClick={() => toggleChannel(c)}
                    data-testid={`channel-${c}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary text-muted-foreground"}`}>
                    <span className={`w-4 h-4 rounded flex items-center justify-center border ${on ? "bg-primary border-primary" : "border-border"}`}>
                      {on && <Check className="w-3 h-3 text-white" />}
                    </span>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          <Label className="block font-bold -mb-2">مؤشرات الأداء (KPIs) — ما نقيسه شهرياً</Label>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {kpis.map((k, i) => {
              const meta = METRIC_META[k.metric] || METRIC_META.likes;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className={`rounded-2xl p-5 border ${meta.bg}`}
                  data-testid={`kpi-card-${k.metric}`}
                >
                  <div className={`flex items-center gap-2 mb-3 font-bold ${meta.color}`}>
                    <meta.icon className="w-5 h-5" /> {k.label}
                  </div>
                  <div className="flex items-end gap-2 mb-3">
                    <Input
                      type="number"
                      value={k.target}
                      onChange={(e) => updateKpi(i, "target", e.target.value)}
                      className="bg-secondary border-border text-2xl font-extrabold h-14"
                      dir="ltr"
                      data-testid={`kpi-target-${k.metric}`}
                    />
                    <span className="text-sm text-muted-foreground mb-3 whitespace-nowrap">{k.unit}</span>
                  </div>
                  {k.rationale && <p className="text-xs text-muted-foreground leading-relaxed">{k.rationale}</p>}
                </motion.div>
              );
            })}
          </div>

          <ActivityKpisCard items={activityKpis} onChange={mark(setActivityKpis)} />

          <CampaignSuggestionsCard items={campaignIdeas} onChange={mark(setCampaignIdeas)} />

          <div className="flex flex-col sm:flex-row gap-3 items-start">
            <StageNav approved={saved && approved} nextLabel="التالي: التنفيذ" nextTo="/app/campaigns" testId="goals"
              approveSlot={<ApproveGoalsButton approved={approved} saving={saving} dirty={!saved} onApprove={() => save(true)} onSaveDraft={() => save(false)} />} />
          </div>
        </div>
      )}
    </div>
  );
}
